import React, { useState, useEffect } from 'react';
import { X, Mail, Users } from 'lucide-react';
import PrimaryButton from './themed/PrimaryButton';
import ThemedCard from './themed/ThemedCard';

interface Table {
  id: number;
  name: string;
  capacity: number;
  guests: Guest[];
}

interface Guest {
  id: string;
  name: string;
  group: string;
  email?: string;
  phone?: string;
  dietaryRestrictions?: string;
  tableId?: number;
  guestCount?: number;
  sourceTableId?: number;
  notificationSent?: boolean;
  notificationFailed?: boolean;
  notificationSentAt?: Date;
}

interface NotificationSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  tables: Table[];
  onSendNotifications: (selectedTables: number[] | 'all') => void;
  loading?: boolean;
}

export const NotificationSelectorModal: React.FC<NotificationSelectorModalProps> = ({
  isOpen,
  onClose,
  tables,
  onSendNotifications,
  loading = false
}) => {
  const [notifyType, setNotifyType] = useState<'all' | 'specific'>('all');
  const [selectedTableIds, setSelectedTableIds] = useState<number[]>([]);

  // 計算統計數據
  const tablesWithEmailGuests = tables.map(table => ({
    ...table,
    emailGuests: table.guests.filter(guest => guest.email && !guest.notificationSent)
  })).filter(table => table.emailGuests.length > 0);

  const totalEmailGuests = tablesWithEmailGuests.reduce(
    (sum, table) => sum + table.emailGuests.length, 0
  );

  const selectedEmailGuests = notifyType === 'all' 
    ? totalEmailGuests
    : tablesWithEmailGuests
        .filter(table => selectedTableIds.includes(table.id))
        .reduce((sum, table) => sum + table.emailGuests.length, 0);

  // 重置狀態當 modal 開啟時
  useEffect(() => {
    if (isOpen) {
      setNotifyType('all');
      setSelectedTableIds([]);
    }
  }, [isOpen]);

  const handleTableSelection = (tableId: number, checked: boolean) => {
    if (checked) {
      setSelectedTableIds(prev => [...prev, tableId]);
    } else {
      setSelectedTableIds(prev => prev.filter(id => id !== tableId));
    }
  };

  const handleSend = () => {
    if (notifyType === 'all') {
      onSendNotifications('all');
    } else {
      onSendNotifications(selectedTableIds);
    }
  };

  const canSend = notifyType === 'all' 
    ? totalEmailGuests > 0 
    : selectedTableIds.length > 0 && selectedEmailGuests > 0;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <ThemedCard className="w-full max-w-md max-h-[80vh] overflow-y-auto bg-white shadow-2xl border border-wedding-pink/20">
        <div className="p-6 bg-white rounded-lg">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <Mail className="h-5 w-5 text-wedding-pink" />
              <h2 className="text-lg font-semibold text-wedding-text-title">選擇通知範圍</h2>
            </div>
            <button 
              onClick={onClose}
              className="text-wedding-text-body hover:text-wedding-text-title"
              disabled={loading}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* 選項區域 */}
          <div className="space-y-4 mb-6">
            {/* 通知所有賓客 */}
            <label className="flex items-center space-x-3 p-3 border border-wedding-pink/20 rounded-lg cursor-pointer hover:bg-wedding-pink/5">
              <input
                type="radio"
                name="notifyType"
                checked={notifyType === 'all'}
                onChange={() => setNotifyType('all')}
                disabled={loading}
                className="text-wedding-pink focus:ring-wedding-pink"
              />
              <div className="flex-1">
                <div className="font-medium text-wedding-text-title">通知所有賓客</div>
                <div className="text-sm text-wedding-text-body flex items-center space-x-1">
                  <Users className="h-4 w-4" />
                  <span>{totalEmailGuests} 位賓客將收到通知</span>
                </div>
              </div>
            </label>

            {/* 通知特定桌次 */}
            <label className="flex items-center space-x-3 p-3 border border-wedding-pink/20 rounded-lg cursor-pointer hover:bg-wedding-pink/5">
              <input
                type="radio"
                name="notifyType"
                checked={notifyType === 'specific'}
                onChange={() => setNotifyType('specific')}
                disabled={loading}
                className="text-wedding-pink focus:ring-wedding-pink"
              />
              <div className="flex-1">
                <div className="font-medium text-wedding-text-title">通知特定桌次</div>
                <div className="text-sm text-wedding-text-body">選擇要通知的桌次</div>
              </div>
            </label>

            {/* 桌次選擇區域 */}
            {notifyType === 'specific' && (
              <div className="ml-6 space-y-2 border-l-2 border-wedding-pink/20 pl-4">
                {tablesWithEmailGuests.length === 0 ? (
                  <div className="text-sm text-wedding-text-body py-2">
                    沒有可通知的桌次（所有賓客都已收到通知或沒有Email）
                  </div>
                ) : (
                  tablesWithEmailGuests.map(table => (
                    <label key={table.id} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedTableIds.includes(table.id)}
                        onChange={(e) => handleTableSelection(table.id, e.target.checked)}
                        disabled={loading}
                        className="text-wedding-pink focus:ring-wedding-pink rounded"
                      />
                      <span className="text-sm text-wedding-text-title">
                        {table.name}
                      </span>
                      <span className="text-xs text-wedding-text-body">
                        ({table.emailGuests.length} 位)
                      </span>
                    </label>
                  ))
                )}
              </div>
            )}
          </div>

          {/* 統計訊息 */}
          <div className="bg-wedding-pink/10 rounded-lg p-3 mb-6">
            <div className="text-sm text-wedding-text-body text-center">
              將發送通知給 <span className="font-semibold text-wedding-pink">{selectedEmailGuests}</span> 位賓客
            </div>
          </div>

          {/* 按鈕區域 */}
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-wedding-text-body hover:bg-gray-50 disabled:opacity-50"
            >
              取消
            </button>
            <PrimaryButton
              onClick={handleSend}
              disabled={!canSend || loading}
              className="flex-1 flex items-center justify-center space-x-2"
            >
              <Mail className="h-4 w-4" />
              <span>{loading ? '發送中...' : '發送通知'}</span>
            </PrimaryButton>
          </div>
        </div>
      </ThemedCard>
    </div>
  );
};